'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetTheses } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { CarouselDots } from '@/components/ui/carousel-dots';
import { cn } from '@/lib/ui/utils';
import { BreakingViewsRow } from './breaking-views-row';
import { HotTakesCardSkeleton } from './hot-takes-card-skeleton';
import { hotTakesSlideMotion } from './hot-takes-slide-motion';
import { HOT_TAKES_AUTO_ADVANCE_MS, useHotTakesCarousel } from './use-hot-takes-carousel';
const DEFAULT_LIMIT = 5 as const;
const MIN_LIMIT = 3 as const;
const MAX_LIMIT = 5 as const;
export interface BreakingViewsWidgetProps {
  /** Number of hot takes in the carousel. Defaults to 5. Clamped to [3, 5]. */
  limit?: 3 | 4 | 5;
  className?: string;
}
export function BreakingViewsWidget(t0) {
  const $ = _c(56);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    limit: t2,
    className
  } = t1;
  const limit = t2 === undefined ? DEFAULT_LIMIT : t2;
  const t = useTranslations("markets.breaking_views");
  const reduceMotion = useReducedMotion();
  const rawLimit = limit;
  const clamped = Math.max(MIN_LIMIT, Math.min(MAX_LIMIT, rawLimit)) as 3 | 4 | 5;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      sort: "hot",
      marketStatus: "open",
      dedupePerMarket: "true"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    rows: hotThesisRows,
    isPending,
    isError
  } = useGetTheses(t3);
  const slideCount = isPending || isError || hotThesisRows.length === 0 ? 0 : Math.min(hotThesisRows.length, clamped);
  const {
    activeIndex,
    direction,
    goTo,
    pause,
    resume
  } = useHotTakesCarousel(slideCount);
  if (isPending) {
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <HotTakesCardSkeleton testId="breaking-views-widget-loading" />;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  if (isError || hotThesisRows.length === 0) {
    return null;
  }
  let t4;
  if ($[4] !== clamped || $[5] !== hotThesisRows) {
    t4 = hotThesisRows.slice(0, clamped);
    $[4] = clamped;
    $[5] = hotThesisRows;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const slides = t4;
  const activeSlide = slides[activeIndex];
  if (activeSlide === undefined) {
    return null;
  }
  const isReduced = reduceMotion === true;
  let t5;
  if ($[7] !== direction || $[8] !== isReduced) {
    t5 = hotTakesSlideMotion(direction, isReduced);
    $[7] = direction;
    $[8] = isReduced;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const slideMotion = t5;
  let t6;
  if ($[10] !== className) {
    t6 = cn("flex flex-col gap-3", className);
    $[10] = className;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = slides.length;
  let t8;
  let t9;
  if ($[12] !== t) {
    t8 = (index, total) => t("carousel_dot_label", {
      index: index + 1,
      total
    });
    t9 = t("carousel_dots_label");
    $[12] = t;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== activeIndex || $[16] !== activeSlide.signature || $[17] !== goTo || $[18] !== slides.length || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className="pointer-events-auto absolute top-4 right-4 z-10"><CarouselDots count={t7} activeIndex={activeIndex} onSelect={goTo} itemAriaLabel={t8} ariaLabel={t9} progressDurationMs={HOT_TAKES_AUTO_ADVANCE_MS} progressResetKey={activeSlide.signature} testId="breaking-views-carousel-dots" dotTestId={_temp} /></div>;
    $[15] = activeIndex;
    $[16] = activeSlide.signature;
    $[17] = goTo;
    $[18] = slides.length;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = activeSlide.signature;
  const t12 = slideMotion.initial;
  const t13 = slideMotion.animate;
  const t14 = slideMotion.exit;
  const t15 = slideMotion.transition;
  const t16 = activeIndex + 1 as 1 | 2 | 3 | 4 | 5;
  const t17 = activeSlide.signature;
  const t18 = activeSlide.marketPubkey;
  const t19 = activeSlide.content;
  const t20 = activeSlide.marketTitle;
  let t21;
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      rasterise: false
    };
    t22 = [];
    $[22] = t21;
    $[23] = t22;
  } else {
    t21 = $[22];
    t22 = $[23];
  }
  let t23;
  if ($[24] !== activeSlide.trader.avatarUrl || $[25] !== activeSlide.trader.displayName || $[26] !== activeSlide.trader.handle || $[27] !== activeSlide.trader.userId || $[28] !== activeSlide.trader.walletAddress) {
    t23 = {
      userId: activeSlide.trader.userId,
      walletAddress: activeSlide.trader.walletAddress,
      displayName: activeSlide.trader.displayName,
      handle: activeSlide.trader.handle,
      avatarUrl: activeSlide.trader.avatarUrl,
      avatarNsfw: false,
      avatarPreferences: t21,
      connectedProviders: t22
    };
    $[24] = activeSlide.trader.avatarUrl;
    $[25] = activeSlide.trader.displayName;
    $[26] = activeSlide.trader.handle;
    $[27] = activeSlide.trader.userId;
    $[28] = activeSlide.trader.walletAddress;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  const t24 = activeSlide.tradeDirection;
  const t25 = Number(activeSlide.tradeAmount);
  let t26;
  if ($[30] !== activeSlide.content || $[31] !== activeSlide.marketPubkey || $[32] !== activeSlide.marketTitle || $[33] !== activeSlide.outcomeLabel || $[34] !== activeSlide.rating || $[35] !== activeSlide.signature || $[36] !== activeSlide.tradeDirection || $[37] !== t16 || $[38] !== t23 || $[39] !== t25) {
    t26 = <BreakingViewsRow position={t16} signature={t17} marketPubkey={t18} content={t19} marketTitle={t20} trader={t23} tradeDirection={t24} tradeAmountD6={t25} outcomeLabel={activeSlide.outcomeLabel} rating={activeSlide.rating} />;
    $[30] = activeSlide.content;
    $[31] = activeSlide.marketPubkey;
    $[32] = activeSlide.marketTitle;
    $[33] = activeSlide.outcomeLabel;
    $[34] = activeSlide.rating;
    $[35] = activeSlide.signature;
    $[36] = activeSlide.tradeDirection;
    $[37] = t16;
    $[38] = t23;
    $[39] = t25;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== activeSlide.signature || $[42] !== slideMotion.animate || $[43] !== slideMotion.exit || $[44] !== slideMotion.initial || $[45] !== slideMotion.transition || $[46] !== t26) {
    t27 = <AnimatePresence mode="wait" initial={false}><m.div key={t11} initial={t12} animate={t13} exit={t14} transition={t15} className="h-full">{t26}</m.div></AnimatePresence>;
    $[41] = activeSlide.signature;
    $[42] = slideMotion.animate;
    $[43] = slideMotion.exit;
    $[44] = slideMotion.initial;
    $[45] = slideMotion.transition;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t10 || $[49] !== t27) {
    t28 = <div className="relative h-34 overflow-hidden" data-testid="breaking-views-carousel">{t10}{t27}</div>;
    $[48] = t10;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== pause || $[52] !== resume || $[53] !== t28 || $[54] !== t6) {
    t29 = <LazyMotion features={domAnimation}><section className={t6} data-testid="breaking-views-widget" onPointerEnter={pause} onPointerLeave={resume} onFocusCapture={pause} onBlurCapture={resume}>{t28}</section></LazyMotion>;
    $[51] = pause;
    $[52] = resume;
    $[53] = t28;
    $[54] = t6;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  return t29;
}
function _temp(index_0) {
  return `breaking-views-carousel-dot-${index_0}`;
}